import React, { useEffect, useMemo } from 'react';
import { Content, TabContent, Header, Title, Wrapper } from './manager-tabs.styles';
import { ArrowsChevronRight } from '@vtbhome/juicyfront/indexIcon';
import { COLORS } from '../../../../shared/config/colors/colors';
import { SegmantTabs } from '../../../../shared/ui/segment-tabs/segment-tabs';
import { ROUTE_ADMIN_CATALOG_COMPLETED, ROUTE_ADMIN_EMPLOYEES } from '../../../../shared/config/routes/routes';
import { CardEmployeeListItemPreview } from '@entities/employee/employee-list-item-preview/ui/card-employee-list-item-preview';
import { useUsersListSharedQuery } from '@entities/user/libs/hooks/use-users-list-shared-query/use-users-list-shared-query';
import { getQueryError } from '@shared/lib/utils/get-query-error/get-query-error';
import { useModalSearch } from '@shared/lib/hooks/use-modal-search/use-modal-search';
import { MODAL_USER_KEY } from '@entities/user/config/search-constants';
import { ModalEmployeeListItemMoreInfo } from '@entities/employee/employee-list-item-more-info/ui/modal-employee-list-item-more-info';
import { managerTabs } from '@pages/main-admin-page/lib/manager-tabs/manager-tabs';
import { useUserProductsListSharedQuery } from '@entities/product/libs/hooks/use-user-products-list-shared-query/use-user-products-list-shared-query';
import { CardOwnOrder } from '@entities/order/ui/card-own-order/card-own-order';
import { useCurrentUserSelector } from '@entities/current-user/model/selectors';
import { ManagerTabValues } from '@pages/main-admin-page/config/manager-constants';
import { DEFAULT_USERS_LIST_PARAMETERS } from '@entities/user/config/store-constants';
import { NetworkStateNew } from '@shared/ui/network-state-new/network-state-new';
import { useUrlTabs } from '@shared/lib/hooks/use-url-tabs/use-url-tabs';
import { usePrefetchProduct } from '@entities/product/api/product-api';
import { usePrefetchOrder } from '@entities/order/api/order-api';
import { listOrdersInProductParameters } from '@shared/lib/utils/list-orders-in-product-parameters/list-orders-in-product-parameters';
import { LinkScroll } from '@shared/ui/link-scroll/link-scroll';
import { DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS } from '@entities/product/config/store-constants';
import { UserProductsListDataRequest } from '@entities/product/api/product-types';

export const ManagerTabs: React.FC = () => {
    const user = useCurrentUserSelector();

    const ordersParameters = useMemo(() => {
        const result: UserProductsListDataRequest = {
            ...DEFAULT_USER_COMPLETED_PRODUCTS_LIST_PARAMETERS,
            userId: user?.id || 0,
        };
        return result;
    }, [user]);

    const [getUsers, getUsersResult] = useUsersListSharedQuery(DEFAULT_USERS_LIST_PARAMETERS);
    const [getOrders, getOrdersResult] = useUserProductsListSharedQuery(ordersParameters);

    const ordersCountTotal = getOrdersResult.data?.total || 0;
    const usersCountTotal = getUsersResult.data?.total || 0;
    const ordersCount = getOrdersResult.data?.items.length || 0;
    const usersCount = getUsersResult.data?.items.length || 0;

    const tabs = useMemo(() => {
        return managerTabs({ productCount: ordersCountTotal, employeeCount: usersCountTotal });
    }, [ordersCountTotal, usersCountTotal]);

    const { tabValue, handleSetTabValue } = useUrlTabs({
        tabValues: Object.values(ManagerTabValues),
        initialTabValue: ManagerTabValues.Employees,
    });

    const prefetchProduct = usePrefetchProduct('productShortInfo', { ifOlderThan: 300 });
    const prefetchOrder = usePrefetchOrder('ordersList', { ifOlderThan: 300 });

    const handlePrefetch = (productId: number) => () => {
        prefetchProduct(productId);
        prefetchOrder(
            listOrdersInProductParameters({
                productId,
                user,
            })
        );
    };

    const { modalSearchValue: modalUserId, setModalSearchValue } = useModalSearch(MODAL_USER_KEY);

    const usersListError = getQueryError(getUsersResult);
    const ordersListError = getQueryError(getOrdersResult);

    useEffect(() => {
        getUsers(DEFAULT_USERS_LIST_PARAMETERS);
    }, []);

    useEffect(() => {
        getOrders(ordersParameters);
    }, [ordersParameters]);

    return (
        <Wrapper>
            <Header>
                <Title>
                    <SegmantTabs activeTabValue={tabValue} list={tabs} onChange={handleSetTabValue} />
                </Title>
                <LinkScroll
                    to={tabValue === ManagerTabValues.Products ? ROUTE_ADMIN_CATALOG_COMPLETED : ROUTE_ADMIN_EMPLOYEES}>
                    <ArrowsChevronRight color={COLORS.neutral_50} />
                </LinkScroll>
            </Header>
            <Content>
                {tabValue === ManagerTabValues.Products && (
                    <TabContent>
                        <NetworkStateNew
                            isInitialized={Boolean(getOrdersResult.data)}
                            isLoading={
                                getOrdersResult.isFetching ||
                                getOrdersResult.isLoading ||
                                getOrdersResult.isUninitialized
                            }
                            isError={getOrdersResult.isError}
                            hasData={Boolean(ordersCount)}
                            isFiltered={Boolean(getOrdersResult.data?.search)}
                            errorProps={{
                                title: 'Ошибка загрузки продуктов',
                                description: ordersListError?.message,
                            }}
                            emptyProps={{
                                title: 'Продукты отсутствуют',
                            }}
                            emptyFilteredProps={{
                                title: 'Продукты не найдены',
                            }}>
                            {getOrdersResult.data?.items.map((order, idx) => (
                                <CardOwnOrder
                                    key={order.id}
                                    product={order}
                                    hasBorder={idx !== ordersCount - 1}
                                    onPrefetchProduct={handlePrefetch(order.id)}
                                />
                            ))}
                        </NetworkStateNew>
                    </TabContent>
                )}
                {tabValue === ManagerTabValues.Employees && (
                    <TabContent>
                        <NetworkStateNew
                            isInitialized={Boolean(getUsersResult.data)}
                            isLoading={
                                getUsersResult.isFetching || getUsersResult.isLoading || getUsersResult.isUninitialized
                            }
                            isError={getUsersResult.isError}
                            hasData={Boolean(usersCount)}
                            isFiltered={Boolean(getUsersResult.data?.search)}
                            errorProps={{
                                title: 'Ошибка загрузки пользователей',
                                description: usersListError?.message,
                            }}
                            emptyProps={{
                                title: 'Пользователи отсутствуют',
                            }}
                            emptyFilteredProps={{
                                title: 'Пользователи не найдены',
                            }}>
                            {getUsersResult.data?.items.map((user, idx) => {
                                return (
                                    <CardEmployeeListItemPreview
                                        key={user.id}
                                        user={user}
                                        hasBorder={idx !== usersCount - 1}
                                        onInfo={() => setModalSearchValue(user.id)}
                                    />
                                );
                            })}
                        </NetworkStateNew>
                    </TabContent>
                )}
            </Content>
            <ModalEmployeeListItemMoreInfo
                isShow={Boolean(modalUserId)}
                userId={modalUserId}
                onClose={() => setModalSearchValue(undefined)}
            />
        </Wrapper>
    );
};
